/**
 * Owner-mode auth handshake (web side). When the Stella app hosts this web app in
 * a WebView for the profile owner, the page asks the native host for the owner's
 * Firebase ID token and the host answers by invoking a receiver we register on
 * `window`. A request/response handshake (rather than reading a pre-injected
 * global) is reliable on both platforms — Android does not guarantee pre-content
 * injection runs before page scripts — and yields a freshly-minted token each call.
 */
declare global {
  interface Window {
    __STELLA_RECEIVE_ID_TOKEN__?: (token: string | null) => void
    ReactNativeWebView?: { postMessage: (message: string) => void }
  }
}

/** True when running inside the Stella app's WebView host (vs a plain browser). */
export function isHostedInApp(): boolean {
  return typeof window !== 'undefined' && !!window.ReactNativeWebView
}

/** Ask the native host to dismiss the share screen (share.tsx handles 'close'). */
export function closeOwnerView(): void {
  window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'close' }))
}

// If the host never answers (it always should when hosted), don't hang forever.
const TOKEN_TIMEOUT_MS = 8000

// Every outstanding request shares ONE persistent receiver. A single global
// receiver that each call overwrote would strand concurrent callers (an earlier
// call's response gets clobbered and it waits out its full timeout). Instead the
// host's response resolves EVERY pending waiter: a freshly-minted token is fungible
// across calls for the same signed-in owner, so any response satisfies them all.
const pending = new Set<(token: string | null) => void>()

function deliverOwnerToken(token: string | null): void {
  const waiters = [...pending]
  pending.clear()
  for (const resolve of waiters) resolve(token)
}

/**
 * Ask the native host for a freshly-minted Firebase ID token. Posts `request_token`
 * and resolves when the host calls `window.__STELLA_RECEIVE_ID_TOKEN__(token)`.
 * Resolves `null` when signed out, not hosted in the app (a plain browser hit), or
 * if the host does not answer in time. Callers use the token immediately for a
 * single authed call, so each call gets a current token (no stale-token reuse).
 * Safe under concurrency: overlapping calls all resolve on the first response.
 */
export function requestOwnerToken(): Promise<string | null> {
  if (typeof window === 'undefined' || !window.ReactNativeWebView) {
    return Promise.resolve(null)
  }
  const host = window.ReactNativeWebView
  if (!window.__STELLA_RECEIVE_ID_TOKEN__) {
    window.__STELLA_RECEIVE_ID_TOKEN__ = deliverOwnerToken
  }
  return new Promise<string | null>((resolve) => {
    let settled = false
    const waiter = (token: string | null) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      pending.delete(waiter)
      resolve(token)
    }
    const timer = window.setTimeout(() => waiter(null), TOKEN_TIMEOUT_MS)
    pending.add(waiter)
    host.postMessage(JSON.stringify({ type: 'request_token' }))
  })
}
